import React, { useState, useRef, useContext, useEffect } from 'react'
import axios from 'axios'
import {
    Box,
    Typography,
    TextField,
    IconButton,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { styled, keyframes } from '@mui/material/styles'
import VenueBox from '../../venue/VenueBox'
import AddIcon from '@mui/icons-material/Add'
import { getBackendUserUrl } from '../../../utils/utils'
import { UserContext } from '../../../contexts/UserContext'
import AudienceSection from './AudienceSection'
import MarketingSection from './MarketingSection'
import EventFormatSection from './EventFormatSection'
import EventDetailsCard from 'components/common/EventDetailsCard'
import { Responsive, WidthProvider } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import PanelModal from './PanelModal'
import CustomPanel from './CustomPanel'
import AgendaPanel from '../agendaTab/AgendaPanel'
import TeamPanel from '../../teamManagement/TeamPanel'
import CustomVenueBox from '../../venue/CustomVenueBox'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { useCommonEventData } from '../../../contexts/EventContext'

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(25, 118, 210, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
`

const ResponsiveGridLayout = WidthProvider(Responsive)

// Custom style for react-grid-layout resize handle
const resizeHandleStyle = `
.rgl .react-resizable-handle {
  width: 28px !important;
  height: 28px !important;
  background: rgba(25, 118, 210, 0.15);
  border-radius: 50%;
  border: 2px solid #1976d2;
  right: 4px;
  bottom: 4px;
  z-index: 10;
  cursor: se-resize;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
}
.rgl .react-resizable-handle:after {
  content: '↔';
  color: #1976d2;
  font-size: 18px;
  font-weight: bold;
  pointer-events: none;
}
.rgl .react-resizable-handle[data-tooltip]:hover:before {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 120%;
  left: 50%;
  transform: translateX(-50%);
  background: #1976d2;
  color: #fff;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 100;
}
`

if (typeof window !== 'undefined' && !document.getElementById('rgl-resize-handle-style')) {
  const style = document.createElement('style')
  style.id = 'rgl-resize-handle-style'
  style.innerHTML = resizeHandleStyle
  document.head.appendChild(style)
}

const PANEL_TYPES = {
    TEXT: 'text',
    TABLE: 'table',
    FILES: 'files',
    USERS: 'users',
    CUSTOM: 'custom'
}

const TABLE_COLUMN_TYPES = {
    TEXT: 'text',
    NUMBER: 'number',
    DATE: 'date',
    SELECT: 'select',
    PERSON: 'person'
}

// New default layouts for built-in panels
const DEFAULT_PANEL_LAYOUTS = {
    lg: [
        { i: 'venueDetails', x: 0, y: 0, w: 5, h: 4 },
        { i: 'audienceInfo', x: 0, y: 4, w: 6, h: 3 },
        { i: 'eventFormat', x: 6, y: 4, w: 6, h: 3 },
        { i: 'agendaPanel', x: 0, y: 7, w: 12, h: 6 },
        { i: 'teamPanel', x: 0, y: 13, w: 12, h: 6 },
        { i: 'marketing', x: 0, y: 19, w: 6, h: 4 },
        { i: 'totalCost', x: 6, y: 19, w: 6, h: 4 },
    ],
    md: [
        { i: 'venueDetails', x: 0, y: 0, w: 10, h: 4 },
        { i: 'audienceInfo', x: 0, y: 4, w: 5, h: 3 },
        { i: 'eventFormat', x: 5, y: 4, w: 5, h: 3 },
        { i: 'agendaPanel', x: 0, y: 7, w: 10, h: 6 },
        { i: 'teamPanel', x: 0, y: 13, w: 10, h: 6 },
        { i: 'marketing', x: 0, y: 19, w: 5, h: 4 },
        { i: 'totalCost', x: 5, y: 19, w: 5, h: 4 },
    ],
    sm: [
        { i: 'venueDetails', x: 0, y: 0, w: 6, h: 4 },
        { i: 'audienceInfo', x: 0, y: 4, w: 6, h: 3 },
        { i: 'eventFormat', x: 0, y: 7, w: 6, h: 3 },
        { i: 'agendaPanel', x: 0, y: 10, w: 6, h: 6 },
        { i: 'teamPanel', x: 0, y: 16, w: 6, h: 6 },
        { i: 'marketing', x: 0, y: 22, w: 6, h: 4 },
        { i: 'totalCost', x: 0, y: 26, w: 6, h: 4 },
    ]
};

// List of built-in panel keys
const BUILT_IN_PANEL_KEYS = [
    'eventDetails',
    'venueDetails',
    'audienceInfo',
    'eventFormat',
    'agendaPanel',
    'teamPanel',
    'marketing',
    'totalCost'
];

const SECTION_KEYS = {
    AUDIENCE: 'audience',
    FORMAT: 'format',
    MARKETING: 'marketing',
};

const EventOverview = ({ eventDetails, setEventDetails, onAddTag, onAgendaTitleClick, venueSpaces }) => {
    const { user } = useContext(UserContext)
    const [normalizedSender, setNormalizedSender] = useState(null)
    const [editingSections, setEditingSections] = useState({
        basics: false,
        [SECTION_KEYS.AUDIENCE]: false,
        [SECTION_KEYS.FORMAT]: false,
        speakers: false,
        [SECTION_KEYS.MARKETING]: false,
    })
    const [isEditing, setIsEditing] = useState(false)

    const eventBasicsRef = useRef(null)
    const audienceInfoRef = useRef(null)
    const targetAudienceRef = useRef(null)
    const venueRef = useRef(null)
    const marketingRef = useRef(null)
    const eventFormatRef = useRef(null)
    const speakersRef = useRef(null)

    const [highlightedField, setHighlightedField] = useState(null)
    const [highlightedSection, setHighlightedSection] = useState(null)

    // Panel state for Add Panel feature
    const [panels, setPanels] = useState([])
    const [isPanelModalOpen, setPanelModalOpen] = useState(false)
    const [editingPanel, setEditingPanel] = useState(null)
    const [newPanel, setNewPanel] = useState({
        title: '',
        type: PANEL_TYPES.TEXT,
        description: '',
        files: [],
        tableConfig: {
            columns: [],
            rows: []
        },
        users: [],
        sections: []
    })

    // Grid layout state
    const [layouts, setLayouts] = useState({
        lg: [
            { i: 'eventDetails', x: 0, y: 0, w: 9, h: 2, static: false, moved: false },
            { i: 'venueDetails', x: 6, y: 0, w: 5, h: 4, static: false, moved: false },
            { i: 'audienceInfo', x: 0, y: 3, w: 5, h: 3, static: false, moved: false },
            { i: 'eventFormat', x: 0, y: 6, w: 5, h: 3, static: false, moved: false },
            { i: 'agendaPanel', x: 0, y: 9, w: 12, h: 6, static: false, moved: false },
            { i: 'teamPanel', x: 0, y: 15, w: 12, h: 6, static: false, moved: false },
            { i: 'marketing', x: 6, y: 6, w: 5, h: 3, static: false, moved: false },
            { i: 'totalCost', x: 0, y: 21, w: 5, h: 4, static: false, moved: false }
        ],
        md: [
            { i: 'eventDetails', x: 0, y: 0, w: 5, h: 2, static: false, moved: false },
            { i: 'venueDetails', x: 0, y: 3, w: 5, h: 4, static: false, moved: false },
            { i: 'audienceInfo', x: 6, y: 3, w: 5, h: 3, static: false, moved: false },
            { i: 'eventFormat', x: 6, y: 4, w: 5, h: 3, static: false, moved: false },
            { i: 'agendaPanel', x: 0, y: 9, w: 10, h: 6, static: false, moved: false },
            { i: 'teamPanel', x: 0, y: 15, w: 10, h: 6, static: false, moved: false },
            { i: 'marketing', x: 0, y: 5, w: 5, h: 3, static: false, moved: false },
            { i: 'totalCost', x: 0, y: 21, w: 5, h: 4, static: false, moved: false }
        ],
        sm: [
            { i: 'eventDetails', x: 0, y: 0, w: 6, h: 2, static: false, moved: false },
            { i: 'venueDetails', x: 0, y: 2, w: 6, h: 4, static: false, moved: false },
            { i: 'audienceInfo', x: 0, y: 6, w: 6, h: 3, static: false, moved: false },
            { i: 'eventFormat', x: 0, y: 9, w: 6, h: 3, static: false, moved: false },
            { i: 'agendaPanel', x: 0, y: 12, w: 6, h: 8, static: false, moved: false },
            { i: 'teamPanel', x: 0, y: 20, w: 6, h: 8, static: false, moved: false },
            { i: 'marketing', x: 0, y: 15, w: 6, h: 3, static: false, moved: false },
            { i: 'totalCost', x: 0, y: 28, w: 6, h: 4, static: false, moved: false }
        ]
    })

    const [menuAnchor, setMenuAnchor] = useState({
        [SECTION_KEYS.AUDIENCE]: null,
        [SECTION_KEYS.FORMAT]: null,
        [SECTION_KEYS.MARKETING]: null,
    });

    const { eventId } = useCommonEventData();

    useEffect(() => {
        if (user) {
            setNormalizedSender({
                userId: user.userId,
                user: {
                    ...user,
                    userId: user.userId,
                    email: user.email,
                },
            })
        }
    }, [user])

    useEffect(() => {
        // Add tooltip to all resize handles
        const handles = document.querySelectorAll('.react-resizable-handle')
        handles.forEach(h => {
            h.setAttribute('data-tooltip', 'Drag to resize')
        })
    }, [panels, layouts])

    const handleEdit = (section) => {
        setEditingSections((prev) => ({ ...prev, [section]: true }))
    }

    const handleSave = async (section, updateData) => {
        setEditingSections((prev) => ({ ...prev, [section]: false }))

        try {
            let endpoint = ''
            let data = {}

            switch (section) {
                case 'basics':
                    endpoint = `/event/${eventId}/update_event_basics`
                    data = updateData
                    break

                case 'audience':
                    endpoint = `/event/${eventId}/update_audience`
                    data = {
                        audienceInfo: updateData.audienceInfo || {},
                    }
                    break

                case 'speakers':
                    endpoint = `/event/${eventId}/update_speakers_and_presenters`
                    const combinedLinkedInUrls = [
                        ...(updateData.format?.linkedinUrls || []),
                        ...(updateData.speakers_and_presenters || []),
                    ]
                    data = {
                        linkedinUrls: combinedLinkedInUrls,
                    }
                    break

                case 'format':
                    endpoint = `/event/${eventId}/update_format`
                    data = {
                        agenda: updateData?.agenda || '',
                        keyActivities: updateData?.keyActivities || '',
                        specialFeatures: updateData?.specialFeatures || '',
                    }
                    break

                case 'marketing':
                    endpoint = `/event/${eventId}/update_marketing`

                    data = {
                        promotional_strategy: updateData.promotional_strategy || '',
                        marketing_channels: updateData.marketing_channels || '',
                    }
                    break

                default:
                    console.warn(`Unhandled section: ${section}`)
                    return
            }

            if (endpoint) {
                console.log('EventOverview:Sending request to:', getBackendUserUrl() + endpoint)
                console.log('EventOverview:With data:', data)

                const response = await axios.put(getBackendUserUrl() + endpoint, data)
                console.log('EventOverview: Response:', response.data)
                if (section === 'basics') {
                    setIsEditing(false)
                }
            }
        } catch (error) {
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                config: error.config,
            })
        }
    }

    const handleChange = (field, value) => {
        setEventDetails((prevData) => {
            const newData = { ...prevData }
            newData.event[field] = value
            return newData
        })
    }

    console.log('eventDetails Overview:', eventDetails);

    const venue = safe_get(eventDetails, 'venue', {})

    // Helper function to safely get nested values
    function safe_get(obj, ...keys) {
        let current = obj
        for (const key of keys) {
            if (current === undefined || current === null) return undefined
            current = current[key]
        }
        return current
    }

    // Calculate total cost
    const totalCost = (
        (eventDetails.event.cost || 0) +
        (eventDetails.event.addons || []).reduce((sum, addon) => {
            // If the addon is a sponsor, subtract its price, otherwise add it
            return addon?.addonType?.includes('Sponsor')
                ? sum - (addon.price || 0)
                : sum + (addon.price || 0)
        }, 0)
    ).toLocaleString()

    const handleTagClick = (tagName) => {
        console.log('Tag clicked:', tagName)

        if (!tagName) {
            console.warn('No tag name provided for click handler')
            return
        }

        // Use the parent component's navigation handler
        onAddTag(tagName)

        let ref = null
        let section = null

        switch (
            tagName.toString().toLowerCase() // Add toString() for safety
        ) {
            case 'event basics':
            case 'date and time':
            case 'event vision':
            case 'event type':
                ref = eventBasicsRef
                section = 'basics'
                break

            case 'target audience':
            case 'audience information':
            case 'demographics':
                ref = audienceInfoRef
                section = 'audience'
                break

            case 'venue':
            case 'location':
                ref = venueRef
                section = 'venue'
                break

            case 'event format':
            case 'agenda overview':
                ref = eventFormatRef
                section = 'format'
                break

            case 'speakers':
            case 'speakers and presenters':
                ref = speakersRef
                section = 'speakers'
                break

            case 'marketing and promotion':
            case 'marketing strategy':
                ref = marketingRef
                section = 'marketing'
                break

            default:
                break
        }

        if (ref?.current) {
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
            // Add highlight effect
            ref.current.style.backgroundColor = 'rgba(25, 118, 210, 0.1)'
            setTimeout(() => {
                ref.current.style.backgroundColor = ''
                setHighlightedField(null)
                setHighlightedSection(null)
            }, 3000)
        }

        if (section) {
            setHighlightedSection(section)
        }
    }

    // Add this function to handle saving basics edits from EventDetailsCard
    const handleSaveBasics = (updated) => {
        // Update the local state with the new values
        setEventDetails((prev) => ({
            ...prev,
            event: {
                ...prev.event,  // Keep all existing event data
                title: updated.eventName || prev.event.title,
                // Only update dates if they have valid values
                eventStartDate: updated.startDate || prev.event.eventStartDate,
                eventEndDate: updated.endDate || prev.event.eventEndDate,
                eventVision: updated.eventVision || prev.event.eventVision,
                type: updated.eventTypes || prev.event.type,
                cost: updated.cost || prev.event.cost,
                guestCount: updated.guestCount || prev.event.guestCount,
                description: updated.description || prev.event.description,
            },
            venue: {
                ...prev.venue,
                city: updated.location || prev.venue?.city,
            },
        }));

        // Prepare data for API call
        const updateData = {
            title: updated.eventName || eventDetails.event.title,
            // Only include dates if they have valid values
            ...(updated.startDate && { eventStartDate: updated.startDate }),
            ...(updated.endDate && { eventEndDate: updated.endDate }),
            eventVision: updated.eventVision || eventDetails.event.eventVision,
            type: updated.eventTypes || eventDetails.event.type,
            cost: updated.cost || eventDetails.event.cost,
            guestCount: updated.guestCount || eventDetails.event.guestCount,
            description: updated.description || eventDetails.event.description,
        };

        // Remove any undefined or null values
        Object.keys(updateData).forEach(key => {
            if (updateData[key] === undefined || updateData[key] === null) {
                delete updateData[key];
            }
        });

        setIsEditing(false);
        handleSave("basics", updateData);  // Pass the cleaned updateData to handleSave
    };

    // Add Panel handlers
    const handleOpenPanelModal = (panel = null) => {
        setEditingPanel(panel);
        setPanelModalOpen(true);
    };
    const handleClosePanelModal = () => {
        setPanelModalOpen(false);
        setEditingPanel(null);
        setNewPanel({
            title: '',
            type: PANEL_TYPES.TEXT,
            description: '',
            files: [],
            tableConfig: {
                columns: [],
                rows: []
            },
            users: [],
            sections: []
        });
    };
    const handlePanelFieldChange = (field, value) => {
        setNewPanel((prev) => ({ ...prev, [field]: value }))
    }

    const handlePanelTypeChange = (type) => {
        setNewPanel(prev => ({
            ...prev,
            type,
            // Reset type-specific fields when changing type
            files: type === PANEL_TYPES.FILES ? prev.files : [],
            tableConfig: type === PANEL_TYPES.TABLE ? prev.tableConfig : { columns: [], rows: [] },
            users: type === PANEL_TYPES.USERS ? prev.users : [],
            sections: type === PANEL_TYPES.CUSTOM ? prev.sections : []
        }))
    }

    const handleFileUpload = (event) => {
        const files = Array.from(event.target.files)
        setNewPanel(prev => ({
            ...prev,
            files: [...prev.files, ...files]
        }))
    }

    const handleRemoveFile = (index) => {
        setNewPanel(prev => ({
            ...prev,
            files: prev.files.filter((_, i) => i !== index)
        }))
    }

    const handleAddTableColumn = () => {
        setNewPanel(prev => ({
            ...prev,
            tableConfig: {
                ...prev.tableConfig,
                columns: [...prev.tableConfig.columns, {
                    id: `col-${Date.now()}`,
                    name: '',
                    type: TABLE_COLUMN_TYPES.TEXT
                }]
            }
        }))
    }

    const handleRemoveTableColumn = (columnId) => {
        setNewPanel(prev => ({
            ...prev,
            tableConfig: {
                ...prev.tableConfig,
                columns: prev.tableConfig.columns.filter(col => col.id !== columnId)
            }
        }))
    }

    const handleAddSection = () => {
        setNewPanel(prev => ({
            ...prev,
            sections: [...prev.sections, {
                id: `section-${Date.now()}`,
                title: '',
                description: ''
            }]
        }))
    }

    // Helper to generate a unique, slugified id from the title
    const getUniquePanelId = (title) => {
        const isDuplicate = layouts.lg.some(l => l.i === title);
        
        if (isDuplicate) {
            // If it's a duplicate, return null to indicate we need to handle this case
            return null;
        }
        
        // If the title is unique, use it as the panel id
        return title;
    }

    // Helper to recalculate custom panel layouts for each breakpoint
    function recalculateCustomPanelLayouts(customPanels, builtInLayouts, breakpoint = 'lg') {
        // Find the max y+h among built-in panels for this breakpoint
        const maxY = builtInLayouts.length > 0 ? Math.max(...builtInLayouts.map(l => l.y + l.h)) : 0;
        const w = breakpoint === 'lg' ? 12 : breakpoint === 'md' ? 10 : 6;
        const h = 4;
        return customPanels.map((panel, idx) => ({
            i: panel.id,
            x: 0,
            y: maxY + idx * h,
            w,
            h,
            static: false,
            moved: false
        }));
    }

    // Helper to merge built-in and custom panel layouts for all breakpoints
    function getMergedLayouts(customPanels) {
        const merged = {};
        ['lg', 'md', 'sm'].forEach(bp => {
            const builtInLayouts = DEFAULT_PANEL_LAYOUTS[bp];
            const customLayouts = recalculateCustomPanelLayouts(customPanels, builtInLayouts, bp);
            merged[bp] = [...builtInLayouts, ...customLayouts];
        });
        return merged;
    }

    // Update the handleSavePanel function to ensure layout data is properly passed
    const handleSavePanel = async (panelData) => {
        try {
            if (editingPanel) {
                const response = await axios.put(
                    getBackendUserUrl() + `/event/${eventId}/panels/${editingPanel.id}`,
                    panelData
                );
                if (response.data) {
                    setPanels(prev => {
                        const updated = prev.map(p => p.id === editingPanel.id ? response.data : p);
                        const customPanels = updated.filter(p => !BUILT_IN_PANEL_KEYS.includes(p.id));
                        setLayouts(getMergedLayouts(customPanels));
                        return updated;
                    });
                }
            } else {
                const newPanelId = getUniquePanelId(panelData.title);
                const newPanelData = {
                    ...panelData,
                    id: newPanelId
                };
                const response = await axios.post(
                    getBackendUserUrl() + `/event/${eventId}/panels`,
                    newPanelData
                );
                if (response.data) {
                    setPanels(prev => {
                        const updated = [...prev, response.data];
                        const customPanels = updated.filter(p => !BUILT_IN_PANEL_KEYS.includes(p.id));
                        setLayouts(getMergedLayouts(customPanels));
                        return updated;
                    });
                }
            }
            handleClosePanelModal();
        } catch (error) {
            console.error('Error saving panel:', error);
        }
    };

    // Add a function to handle panel deletion
    const handleDeletePanel = async (panelId) => {
        try {
            await axios.delete(getBackendUserUrl() + `/event/${eventId}/panels/${panelId}`)
            setPanels(prev => {
                const updated = prev.filter(p => p.id !== panelId);
                const customPanels = updated.filter(p => !BUILT_IN_PANEL_KEYS.includes(p.id));
                setLayouts(getMergedLayouts(customPanels));
                return updated;
            });
        } catch (error) {
            console.error('Error deleting panel:', error)
        }
    }

    const onLayoutChange = (layout, layouts) => {
        // Custom logic to prevent overlap while allowing resizing
        const newLayouts = {};
        
        Object.entries(layouts).forEach(([breakpoint, panelsLayout]) => {
            // Sort panels by y position, then by x position
            const sortedPanels = [...panelsLayout].sort((a, b) => {
                if (a.y === b.y) return a.x - b.x;
                return a.y - b.y;
            });
            
            const adjustedPanels = [];
            let currentY = 0;
            
            for (let i = 0; i < sortedPanels.length; i++) {
                const panel = { ...sortedPanels[i] };
                
                // For the first panel, start at y = 0
                if (i === 0) {
                    panel.y = 0;
                    currentY = panel.h;
                } else {
                    const prevPanel = adjustedPanels[i - 1];
                    
                    // Check if this panel can fit on the same row as the previous panel
                    const canFitOnSameRow = (
                        panel.x >= prevPanel.x + prevPanel.w && // No horizontal overlap
                        panel.y === prevPanel.y // Originally on the same row
                    );
                    
                    if (canFitOnSameRow) {
                        // Keep on the same row
                        panel.y = prevPanel.y;
                        // Update currentY to be the max height of panels in this row
                        currentY = Math.max(currentY, prevPanel.y + Math.max(prevPanel.h, panel.h));
                    } else {
                        // Move to next row
                        panel.y = currentY;
                        currentY = panel.y + panel.h;
                    }
                }
                
                adjustedPanels.push(panel);
            }
            
            newLayouts[breakpoint] = adjustedPanels;
        });
        
        setLayouts(newLayouts);
    };

    // Update the useEffect for loading panels to ensure layout data is properly applied
    useEffect(() => {
        const loadPanels = async () => {
            try {
                const response = await axios.get(getBackendUserUrl() + `/event/${eventId}/panels`);
                if (response.data) {
                    setPanels(response.data);
                    const customPanels = response.data.filter(p => !BUILT_IN_PANEL_KEYS.includes(p.id));
                    setLayouts(getMergedLayouts(customPanels));
                }
            } catch (error) {
                console.error('Error loading panels:', error);
            }
        };

        if (eventId) {
            loadPanels();
        }
    }, [eventId]);

    const handleMenuOpen = (section, event) => {
        setMenuAnchor((prev) => ({ ...prev, [section]: event.currentTarget }));
    };
    const handleMenuClose = (section) => {
        setMenuAnchor((prev) => ({ ...prev, [section]: null }));
    };
    const handleMenuEdit = (section) => {
        handleMenuClose(section);
        handleEdit(section);
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Event Details Card at the top, not draggable */}
            <EventDetailsCard
                eventDetails={{
                    eventName: eventDetails.event?.title || '',
                    eventVision: eventDetails.event?.vision || '',
                    description: eventDetails.event?.description || '',
                    // eventRationale: eventDetails.event?.rationale || '',
                    eventTypes: eventDetails.event?.type || [],
                    startDate: eventDetails.event?.eventStartDate || '',
                    endDate: eventDetails.event?.eventEndDate || '',
                    location: eventDetails.venue?.city || '',
                    guestCount: eventDetails.event?.guestCount || '',
                }}
                isEditing={isEditing}
                onEdit={() => setIsEditing(true)}
                onCancel={() => setIsEditing(false)}
                // onSave={handleSave}
                onSave={handleSaveBasics}
                imageUrl={eventDetails.venue?.images?.[0]?.url || 'default-image-url.jpg'}
                sx={{ height: '100%', mb: 4 }}
            />
            <ResponsiveGridLayout
                className="layout"
                layouts={layouts}
                breakpoints={{ lg: 1200, md: 996, sm: 768 }}
                cols={{ lg: 12, md: 10, sm: 6 }}
                rowHeight={60}
                width={1200}
                onLayoutChange={onLayoutChange}
                margin={[20, 20]}
                containerPadding={[20, 20]}
                preventCollision={false}
                compactType={null}
                draggableHandle=".panel-drag-handle"
            >
                <Box key="venueDetails" sx={theme => theme.custom.dashboardPanel}>
                    <Box className="panel-drag-handle" sx={theme => theme.custom.dashboardPanelHeader}>
                        <DragIndicatorIcon sx={{ mr: 1, color: '#b3c6e0' }} fontSize="small" />
                        Venue Details
                    </Box>
                    <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                        <CustomVenueBox
                            venueData={eventDetails.venue}
                            highlightedSection={highlightedSection}
                            venueRef={venueRef}
                        />
                    </Box>
                </Box>

                <Box key="audienceInfo" sx={theme => theme.custom.dashboardPanel}>
                    <Box sx={theme => ({ ...theme.custom.dashboardPanelHeader, display: 'flex', alignItems: 'center' })}>
                        <Box className="panel-drag-handle" sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', userSelect: 'none' }}>
                            <DragIndicatorIcon sx={{ mr: 1, color: '#b3c6e0' }} fontSize="small" />
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 600,
                                fontSize: '1rem',
                                color: 'text.primary',
                                ml: 0.5,
                            }}
                        >
                            Audience Information
                        </Box>
                        <Box sx={{ flexGrow: 1 }} />
                        {editingSections[SECTION_KEYS.AUDIENCE] ? (
                            <IconButton
                                onClick={() => handleSave(SECTION_KEYS.AUDIENCE, eventDetails.event)}
                                sx={{ ml: 1, p: 0.5, width: 32, height: 32 }}
                            >
                                <SaveIcon />
                            </IconButton>
                        ) : (
                            <>
                                <IconButton
                                    onClick={(e) => handleMenuOpen(SECTION_KEYS.AUDIENCE, e)}
                                    sx={{ ml: 1, p: 0.5, width: 32, height: 32 }}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                                <Menu
                                    anchorEl={menuAnchor[SECTION_KEYS.AUDIENCE]}
                                    open={Boolean(menuAnchor[SECTION_KEYS.AUDIENCE])}
                                    onClose={() => handleMenuClose(SECTION_KEYS.AUDIENCE)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                >
                                    <MenuItem onClick={() => handleMenuEdit(SECTION_KEYS.AUDIENCE)}>
                                        <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </Box>
                    <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                        <AudienceSection
                            eventDetails={eventDetails}
                            handleChange={handleChange}
                            isEditing={editingSections[SECTION_KEYS.AUDIENCE]}
                        />
                    </Box>
                </Box>

                {/* <Box key="speakers" sx={theme => theme.custom.dashboardPanel}>
                    <Box sx={theme => ({ ...theme.custom.dashboardPanelHeader, display: 'flex', alignItems: 'center' })}>
                        <Box className="panel-drag-handle" sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', userSelect: 'none' }}>
                            <DragIndicatorIcon sx={{ mr: 1, color: '#b3c6e0' }} fontSize="small" />
                            Speakers and Presenters
                        </Box>
                        <Box sx={{ flexGrow: 1 }} />
                        {!editingSections.speakers && (
                            <IconButton onClick={() => handleEdit('speakers')} sx={{ ml: 1 }}><EditIcon /></IconButton>
                        )}
                        {editingSections.speakers && (
                            <IconButton onClick={() => handleSave('speakers')} sx={{ ml: 1 }}><SaveIcon /></IconButton>
                        )}
                    </Box>
                    <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                        <SpeakersSection
                            eventDetails={eventDetails}
                            setEventDetails={setEventDetails}
                            isEditing={editingSections.speakers}
                            onEdit={() => handleEdit('speakers')}
                            onSave={() => handleSave('speakers')}
                        />
                    </Box>
                </Box> */}

                <Box key="eventFormat" sx={theme => theme.custom.dashboardPanel}>
                    <Box sx={theme => ({ ...theme.custom.dashboardPanelHeader, display: 'flex', alignItems: 'center' })}>
                        <Box className="panel-drag-handle" sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', userSelect: 'none' }}>
                            <DragIndicatorIcon sx={{ mr: 1, color: '#b3c6e0' }} fontSize="small" />
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 600,
                                fontSize: '1rem',
                                color: 'text.primary',
                                ml: 0.5,
                            }}
                        >
                            Event Format
                        </Box>
                        <Box sx={{ flexGrow: 1 }} />
                        {editingSections[SECTION_KEYS.FORMAT] ? (
                            <IconButton
                                onClick={() => handleSave(SECTION_KEYS.FORMAT, eventDetails.event)}
                                sx={{ ml: 1, p: 0.5, width: 32, height: 32 }}
                            >
                                <SaveIcon />
                            </IconButton>
                        ) : (
                            <>
                                <IconButton
                                    onClick={(e) => handleMenuOpen(SECTION_KEYS.FORMAT, e)}
                                    sx={{ ml: 1, p: 0.5, width: 32, height: 32 }}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                                <Menu
                                    anchorEl={menuAnchor[SECTION_KEYS.FORMAT]}
                                    open={Boolean(menuAnchor[SECTION_KEYS.FORMAT])}
                                    onClose={() => handleMenuClose(SECTION_KEYS.FORMAT)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                >
                                    <MenuItem onClick={() => handleMenuEdit(SECTION_KEYS.FORMAT)}>
                                        <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </Box>
                    <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                        <EventFormatSection
                            eventDetails={eventDetails}
                            handleChange={handleChange}
                            isEditing={editingSections[SECTION_KEYS.FORMAT]}
                        />
                    </Box>
                </Box>

                {/* Agenda Panel */}
                <Box key="agendaPanel" sx={theme => theme.custom.dashboardPanel}>
                <Box sx={theme => ({ ...theme.custom.dashboardPanelHeader, display: 'flex', alignItems: 'center' })}>
                        <Box className="panel-drag-handle" sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', userSelect: 'none' }}>
                            <DragIndicatorIcon sx={{ mr: 1, color: '#b3c6e0' }} fontSize="small" />
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 600,
                                fontSize: '1rem',
                                color: 'text.primary',
                                cursor: 'pointer',
                                ml: 0.5,
                            }}
                        >
                            Agenda
                        </Box>
                    </Box>
                    <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                        <AgendaPanel eventId={eventId} />
                    </Box>
                </Box>

                {/* Team Panel */}
                <Box key="teamPanel" sx={theme => theme.custom.dashboardPanel}>
                <Box sx={theme => ({ ...theme.custom.dashboardPanelHeader, display: 'flex', alignItems: 'center' })}>
                        <Box className="panel-drag-handle" sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', userSelect: 'none' }}>
                            <DragIndicatorIcon sx={{ mr: 1, color: '#b3c6e0' }} fontSize="small" />
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 600,
                                fontSize: '1rem',
                                color: 'text.primary',
                                ml: 0.5,
                            }}
                        >
                            Team
                        </Box>
                    </Box>
                    <Box sx={{ flex: 1, p: 2, overflow: 'auto', width: '100%', height: '100%' }}>
                        <TeamPanel eventId={eventId} />
                    </Box>
                </Box>

                <Box key="marketing" sx={theme => theme.custom.dashboardPanel}>
                    <Box sx={theme => ({ ...theme.custom.dashboardPanelHeader, display: 'flex', alignItems: 'center' })}>
                        <Box className="panel-drag-handle" sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', userSelect: 'none' }}>
                            <DragIndicatorIcon sx={{ mr: 1, color: '#b3c6e0' }} fontSize="small" />
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 600,
                                fontSize: '1rem',
                                color: 'text.primary',
                                ml: 0.5,
                            }}
                        >
                            Marketing and Promotion
                        </Box>
                        <Box sx={{ flexGrow: 1 }} />
                        {editingSections[SECTION_KEYS.MARKETING] ? (
                            <IconButton
                                onClick={() => handleSave(SECTION_KEYS.MARKETING, eventDetails.event)}
                                sx={{ ml: 1, p: 0.5, width: 32, height: 32 }}
                            >
                                <SaveIcon />
                            </IconButton>
                        ) : (
                            <>
                                <IconButton
                                    onClick={(e) => handleMenuOpen(SECTION_KEYS.MARKETING, e)}
                                    sx={{ ml: 1, p: 0.5, width: 32, height: 32 }}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                                <Menu
                                    anchorEl={menuAnchor[SECTION_KEYS.MARKETING]}
                                    open={Boolean(menuAnchor[SECTION_KEYS.MARKETING])}
                                    onClose={() => handleMenuClose(SECTION_KEYS.MARKETING)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                >
                                    <MenuItem onClick={() => handleMenuEdit(SECTION_KEYS.MARKETING)}>
                                        <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </Box>
                    <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                        <MarketingSection
                            eventDetails={eventDetails}
                            handleChange={handleChange}
                            isEditing={editingSections[SECTION_KEYS.MARKETING]}
                        />
                    </Box>
                </Box>

                <Box key="totalCost" sx={theme => theme.custom.dashboardPanel}>
                    <Box className="panel-drag-handle" sx={theme => theme.custom.dashboardPanelHeader}>
                        <DragIndicatorIcon sx={{ mr: 1, color: '#b3c6e0' }} fontSize="small" />
                        Total Costs Breakdown
                    </Box>
                    <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                        <Typography variant="h6" component="h2" gutterBottom>
                            Total Costs Breakdown
                        </Typography>
                        {/* Venue Cost */}
                        <Box display="flex" justifyContent="space-between" mb={2}>
                            <Typography variant="body1">Venue:</Typography>
                            <Typography variant="body1">
                                ${eventDetails.event.cost ? eventDetails.event.cost.toLocaleString() : 0}
                            </Typography>
                        </Box>
                        {/* Addons and Sponsors */}
                        {eventDetails.event.addons.map((addon, index) => (
                            <Box
                                key={index}
                                sx={{
                                    pl: 2,
                                    mb: 2,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                }}
                            >
                                <Box>
                                    <Typography variant="body2" display="block" gutterBottom>
                                        {addon.addonType}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Pending: {addon.company?.name}
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        ml: 2,
                                        color: addon.addonType?.toLowerCase().includes('sponsor')
                                            ? 'success.main'
                                            : 'text.primary',
                                        fontWeight: addon.addonType?.toLowerCase().includes('sponsor')
                                            ? 'bold'
                                            : 'normal',
                                    }}
                                >
                                    {addon.addonType?.toLowerCase().includes('sponsor') ? '-' : ''}$
                                    {addon.price ? addon.price.toLocaleString() : 0}
                                </Typography>
                            </Box>
                        ))}
                        {/* Total Cost */}
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            mt={2}
                            pt={2}
                            sx={{ borderTop: 1, borderColor: 'divider' }}
                        >
                            <Typography variant="h6">Total Cost</Typography>
                            <Typography variant="h6" sx={{ ml: 2 }}>
                                ${totalCost.toLocaleString()}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Custom panels */}
                {panels.map((panel) => (
                    <Box 
                        key={panel.id} 
                        sx={theme => ({
                            ...theme.custom.dashboardPanel,
                            position: 'relative',
                            zIndex: 2,
                            '&:hover': {
                                zIndex: 3
                            }
                        })}
                    >
                        <CustomPanel 
                            panel={panel}
                            onEdit={(panel) => handleOpenPanelModal(panel)}
                            onDelete={handleDeletePanel}
                        />
                    </Box>
                ))}
            </ResponsiveGridLayout>

            {/* Add Panel Button */}
            <Box
                onClick={() => handleOpenPanelModal()}
                sx={{
                    mt: 2,
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    position: 'relative',
                    zIndex: 1,
                    '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover',
                    },
                }}
            >
                <AddIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="h6" color="text.secondary">
                    Add New Panel
                </Typography>
            </Box>

            {/* Add Panel Modal */}
            <PanelModal
                open={isPanelModalOpen}
                onClose={handleClosePanelModal}
                panel={editingPanel}
                onSave={handleSavePanel}
                isEditing={!!editingPanel}
            />
        </Box>
    )
}

export default EventOverview
